import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';

import { Store } from '@ngrx/store';

import { Observable, Subscription } from 'rxjs';

import { APP_ROUTES } from 'src/app/app.routes';
import { Study } from 'src/app/models/study';
import { Role } from 'src/app/models/role';
import { Language } from 'src/app/models/language';
import { AppState } from 'src/app/state';
import { User } from 'src/app/models/user';
import { selectUser } from 'src/app/state/user/user.selectors';
import { DASHBOARD_ROUTES } from 'src/app/dashboard/dashboard.routes';
import { SETTINGS_ROUTES } from '../../../dashboard/settings/settings.routes';
import { YoutubeDialogComponent } from 'src/app/dashboard/studies/components/youtube-dialog/youtube-dialog.component';
import {
  selectSelectedLanguage,
  selectSelectedStudy,
  selectSupportedLanguages,
} from '../../../dashboard/state/study/study.selectors';
import { setSelectedLanguage } from '../../../dashboard/state/study/study.actions';
import { StudyType } from '../../../models/study-type';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { logout } from '../../../state/user/user.actions';

import { environment } from 'src/environments/environment';

@Component({
  selector: 'dashboard-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  breadcrumbs$: Observable<string[]>;
  user: User;

  titles = {
    default: 'BMS LAB Dashboard',
    regular: 'Twente Intervention and Interaction Machine',
    careercompass: 'Career Compass',
  };
  images = {
    default: 'assets/images/bms-lab.png',
    regular: 'assets/images/tiim-logo.png',
    careercompass: 'assets/images/cc-logo.png',
  };

  role = Role;

  selectedStudy: Study;
  selectedStudyTranslations = [];

  supportedLanguages$: Observable<Language[]>;
  selectedLanguage$: Observable<Language>;

  private subscriptions$ = new Subscription();

  StudyType = StudyType;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    public store: Store<AppState>,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.supportedLanguages$ = this.store.select(selectSupportedLanguages);
    this.selectedLanguage$ = this.store.select(selectSelectedLanguage);

    this.subscriptions$.add(
      this.store.select(selectSelectedStudy).subscribe((study) => {
        this.selectedStudy = study;
        this.selectedStudyTranslations = this.selectedStudy?.translations.filter(
          (translation) => translation.identifier === 'title'
        );
        this.breadcrumbs$ = this.buildBreadCrumb(this.activatedRoute.root);
      })
    );
    this.subscriptions$.add(this.store.select(selectUser).subscribe((user) => (this.user = user)));

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.breadcrumbs$ = this.buildBreadCrumb(this.activatedRoute.root);
      });
  }

  navigateHome(): void {
    this.router.navigate([APP_ROUTES.ROOT]);
  }

  navigateToSettings(): void {
    this.router.navigate([`/${DASHBOARD_ROUTES.SETTINGS}/${SETTINGS_ROUTES.STATUS}`]);
  }

  changeActiveLanguage(selectedLanguage: Language): void {
    this.store.dispatch(setSelectedLanguage({ selectedLanguage }));
  }

  openYoutubeDialog() {
    this.dialog.open(YoutubeDialogComponent);
  }

  buildBreadCrumb(route: ActivatedRoute, url: string = '', breadcrumbs = []) {
    const label = route.routeConfig && route.routeConfig.data ? route.routeConfig.data.breadcrumb : '';
    let path = route.routeConfig && route.routeConfig.data ? route.routeConfig.path : '';

    let newBreadcrumbs = [];
    let idBreadcrumb = {};

    const firstRoutePart = path.split('/')[0];
    const lastRoutePart = path.split('/')[1];
    const isDynamicRoute = firstRoutePart?.startsWith(':') || lastRoutePart?.startsWith(':');

    if (isDynamicRoute && !!route.snapshot) {
      // Replacement of :id with actual if
      let partToReplace = '';
      if (firstRoutePart?.startsWith(':')) {
        partToReplace = firstRoutePart;
      } else {
        partToReplace = lastRoutePart;
      }
      const paramName = partToReplace.split(':')[1];
      path = path.replace(partToReplace, route.snapshot.params[paramName]);

      // New dynamic breadcrumb
      if (partToReplace === firstRoutePart) {
        // Study
        const idLabel = route.snapshot.params[paramName];
        const detailsPath = path.replace(lastRoutePart, 'details');
        idBreadcrumb = {
          label: this.selectedStudyTranslations ? this.selectedStudyTranslations[0].translation.toUpperCase() : idLabel,
          url: `${url}/${detailsPath}`,
        };
      } else {
        // Single resource (participant or researcher)
        idBreadcrumb = {
          label: firstRoutePart.toUpperCase(),
          url: `${url}/${firstRoutePart}`,
        };
      }
    }

    const nextUrl = path ? `${url}/${path}` : url;

    const breadcrumb = {
      label,
      url: nextUrl,
    };

    newBreadcrumbs = breadcrumb.label
      ? isDynamicRoute
        ? [...breadcrumbs, idBreadcrumb, breadcrumb]
        : [...breadcrumbs, breadcrumb]
      : [...breadcrumbs];

    if (route.firstChild) {
      return this.buildBreadCrumb(route.firstChild, nextUrl, newBreadcrumbs);
    }

    // Special case for STUDIES breadcrumb
    if (newBreadcrumbs[newBreadcrumbs.length - 1]?.label === ' ' && newBreadcrumbs.length === 2) {
      newBreadcrumbs.pop();
    }

    return newBreadcrumbs;
  }

  logout() {
    this.store.dispatch(logout({ email: this.user?.email }));
  }

  openPrivacyPolicy() {
    window.open(environment.privacyUrl, '_blank');
  }

  ngOnDestroy(): void {
    this.subscriptions$.unsubscribe();
  }
}
